import React from 'react';

import { Flag, Home, ListChecks, SlidersHorizontal, X } from 'lucide-react';

import PlaceSelect, { PlaceOption } from './PlaceSelect';

export type { PlaceOption } from './PlaceSelect';

// The member's own three lists, as the dashboard map's legend names and draws them. That legend
// only explains the pin badges; these are the same three made clickable, so a member reads the
// two surfaces the same way round.
export type CourseFilterKind = 'home' | 'played' | 'bucket';

export const COURSE_FILTER_KINDS: CourseFilterKind[] = [
  'home',
  'played',
  'bucket',
];

export const KIND_LABEL: Record<CourseFilterKind, string> = {
  home: 'Home course',
  played: 'Played',
  bucket: 'Bucket list',
};

export const KIND_ICON: Record<
  CourseFilterKind,
  React.ComponentType<{ className?: string }>
> = {
  home: Home,
  played: Flag,
  bucket: ListChecks,
};

export interface GeoFilter {
  /** Country name as the course rows spell it, or null for every country. */
  country: string | null;
  /** State, region or province within that country, or null for all of them. */
  region: string | null;
}

export const EMPTY_GEO_FILTER: GeoFilter = { country: null, region: null };

/** How many separate narrowings are applied. Drives the count on the button. */
export const countActiveFilters = (
  kinds: Set<CourseFilterKind>,
  geo: GeoFilter,
): number =>
  kinds.size + (geo.country != null ? 1 : 0) + (geo.region != null ? 1 : 0);

interface CourseFilterButtonProps {
  activeCount: number;
  onClick: () => void;
  isOpen: boolean;
}

// The control that leads the chip row under the search box. Separate from the drawer because they
// live in different places in the tree: this sits in the row, and the drawer has to be a child of
// the page so it can run the full height of it.
export const CourseFilterButton: React.FC<CourseFilterButtonProps> = ({
  activeCount,
  onClick,
  isOpen,
}) => {
  const isSet = activeCount > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={isOpen}
      aria-label={
        isSet ? `Filter, ${activeCount} applied` : 'Filter the courses'
      }
      // A pill with the word, first in the chip row under the search box (the client's call,
      // 2026-11-04). Same height and shell as the chips beside it (ExploreFilterChips), so the
      // row reads as one set.
      className={`relative flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium shadow-lg backdrop-blur transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        isSet
          ? // Gold fill takes black text. BRAND.md: white on gold and gold on gold are both
            // 2.32:1, so neither is allowed.
            'border-primary bg-primary text-secondary'
          : 'border-primary/30 bg-charcoal/90 text-primary hover:border-primary hover:bg-charcoal'
      }`}
      title={isSet ? `Filter, ${activeCount} applied` : 'Filter the courses'}
    >
      <SlidersHorizontal className="h-4 w-4" />
      <span>Filters</span>
      {/* How many are on, not which. Which ones is what opening the drawer is for. Inside the
          pill, after the word: the fill is already gold when set, so a black disc with a gold
          number keeps it readable (BRAND.md: never gold on gold). */}
      {isSet && (
        <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-secondary px-1 text-[11px] font-bold text-primary">
          {activeCount}
        </span>
      )}
    </button>
  );
};

interface CourseFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** The member's lists currently switched on. Empty means no list filter, which is every course. */
  kinds: Set<CourseFilterKind>;
  onToggleKind: (kind: CourseFilterKind) => void;
  /** How many courses the member has of each kind. */
  counts: Record<CourseFilterKind, number>;
  /** Whether to offer the member's own lists at all. False for a signed-out visitor. */
  showKinds: boolean;
  geo: GeoFilter;
  onGeoChange: (next: GeoFilter) => void;
  /** Every country present in the course set, already sorted. */
  countries: PlaceOption[];
  /** The regions inside the chosen country, already sorted. Empty when no country is chosen. */
  regions: PlaceOption[];
  onClearAll: () => void;
  /**
   * How many courses the filter currently leaves on the map. Reported in the footer as a result,
   * not offered as an action: there is nothing to apply, because every change here is already on
   * the map behind the drawer.
   */
  resultCount: number;
}

// Every way of narrowing the map, in one drawer.
//
// A drawer rather than the dropdown panel it started as: the panel was 18rem hanging off a button
// at the top of a 375px screen, so the country list covered the map it was filtering and the
// panel itself had nowhere to grow. This is the same shape the detail drawer and the member gate
// already use on this page, so it is a pattern a member has met before, and it has room for the
// sections to breathe.
//
// Deliberately not components/common/Drawer.tsx, for the same reason those two are not: that one
// is modal, and its full-screen backdrop and page-scroll lock swallow map panning.
//
// It stays mounted and parks off-screen under a transform so it animates both ways, again like
// its two neighbours. aria-hidden and pointer-events keep it out of reach while it is shut.
//
// The lists are a union among themselves (Played plus Bucket list is both lists, not the overlap:
// a course on two of them is not a question anyone has) and an "and" against the place, so Played
// plus Scotland is the courses you have played in Scotland.
//
// Region is the country's child and stays disabled until a country is picked. Every region in the
// world in one list is not usable, and "Western Cape" means nothing without "South Africa" above
// it.
//
// Names, not ids, for both selects: course rows carry countryName and stateName and nothing else,
// so the name is the only key there is. It also means no lookup call, since every option offered
// here was read off the courses already on the page.
const CourseFilterDrawer: React.FC<CourseFilterDrawerProps> = ({
  isOpen,
  onClose,
  kinds,
  onToggleKind,
  counts,
  showKinds,
  geo,
  onGeoChange,
  countries,
  regions,
  onClearAll,
  resultCount,
}) => {
  const isSet = countActiveFilters(kinds, geo) > 0;

  const sectionLabel =
    'mb-2 block text-[11px] font-semibold uppercase tracking-wider text-primary/80';

  return (
    <aside
      aria-hidden={!isOpen}
      aria-label="Filter the courses"
      className={`fixed inset-y-0 right-0 z-[55] flex w-full flex-col border-l border-primary/20 bg-charcoal shadow-2xl transition-transform duration-300 ease-in-out sm:w-96 ${
        isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-white">
          <SlidersHorizontal className="h-4 w-4 text-primary" />
          Filter
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-white/50 transition-colors hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Scrolls on its own, so a long country list never pushes the footer off the bottom. */}
      <div className="flex-1 overflow-y-auto px-4 py-5">
        {/* Signed-out visitors do not have these lists, so every chip would be an empty map. */}
        {showKinds && (
          <div className="mb-6">
            <span className={sectionLabel}>My courses</span>
            <div className="flex flex-wrap gap-2">
              {COURSE_FILTER_KINDS.map((kind) => {
                const Icon = KIND_ICON[kind];
                const isActive = kinds.has(kind);
                const count = counts[kind];
                const isEmpty = count === 0;

                return (
                  <button
                    key={kind}
                    type="button"
                    // A chip with nothing behind it is disabled rather than hidden: a member with
                    // no bucket list still gets told the bucket list exists.
                    disabled={isEmpty && !isActive}
                    onClick={() => onToggleKind(kind)}
                    // aria-pressed, not a checkbox role: these are toggle buttons, and a screen
                    // reader should say "Played, pressed" rather than announce a checkbox.
                    aria-pressed={isActive}
                    className={`flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? 'border-primary bg-primary font-semibold text-secondary'
                        : 'border-white/15 text-white/80 hover:border-primary/60 disabled:cursor-not-allowed disabled:border-white/10 disabled:text-white/30 disabled:hover:border-white/10'
                    }`}
                  >
                    {/* The same black disc with a gold glyph the dashboard map puts on its pins,
                        so a chip and a pin badge are visibly the same thing. */}
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary ring-1 ring-primary">
                      <Icon className="h-3 w-3 text-primary" />
                    </span>
                    {KIND_LABEL[kind]}
                    {/* The count is the useful half of the label here: it says whether the chip
                        is worth pressing before it is pressed. */}
                    <span
                      className={
                        isActive ? 'text-secondary/70' : 'text-white/40'
                      }
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-5">
          <span className={sectionLabel}>Country</span>
          <PlaceSelect
            label="country"
            placeholder="All countries"
            allLabel="All countries"
            options={countries}
            value={geo.country}
            // Changing country drops the region with it. A region from the country before is not
            // in the new country's list, so keeping it would filter to nothing at all.
            onChange={(next) => onGeoChange({ country: next, region: null })}
            kind="country"
            // Two hundred countries is past the point where scrolling is the answer.
            searchable
          />
        </div>

        <div>
          <span className={sectionLabel}>Region or province</span>
          <PlaceSelect
            label="region"
            placeholder={
              geo.country == null ? 'Pick a country first' : 'All regions'
            }
            allLabel="All regions"
            options={regions}
            value={geo.region}
            onChange={(next) => onGeoChange({ ...geo, region: next })}
            kind="region"
            disabled={geo.country == null || regions.length === 0}
          />
        </div>
      </div>

      {/* The filter applies the moment it is set, so nothing down here submits anything.
          The count used to sit on the gold button as "Show 602", which read as an apply button
          for work that had already happened. It is a status line now, above the buttons, and the
          button says the only thing it actually does. */}
      <div
        className="border-t border-white/10 px-4 py-4"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <p
          className="mb-3 text-sm text-white/60"
          // Announced when it changes, so a member using a screen reader hears the result of a
          // pick without going looking for it.
          aria-live="polite"
        >
          <span className="font-semibold text-white">
            {resultCount.toLocaleString('en-US')}
          </span>{' '}
          {resultCount === 1 ? 'course' : 'courses'}{' '}
          {isSet ? 'match' : 'on the map'}
        </p>

        <div className="flex gap-2">
          {isSet && (
            <button
              type="button"
              onClick={onClearAll}
              className="h-11 flex-1 rounded-lg border border-white/15 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10"
            >
              Clear all
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="h-11 flex-1 rounded-lg bg-primary text-sm font-semibold text-secondary transition-opacity hover:opacity-90"
          >
            Done
          </button>
        </div>
      </div>
    </aside>
  );
};

export default CourseFilterDrawer;
